import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UAParser } from 'ua-parser-js'

import { BaseModalType } from '@context/ModalsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { Body1 } from '@ui/Typography'
import { Divider } from '@ui/components/Divider'
import { Input } from '@ui/components/Input'
import { Link } from '@ui/components/Link'
import { Modal } from '@ui/components/Modal'
import { RichIcon } from '@ui/components/RichIcon'
import { CopyIcon } from '@ui/icons/Copy.icon'

import { copyToClipboard } from '@utils/navigator.utils'

import { GoogleAuthenticator2FAProviderDataDto } from '@model/TwoFactorAuthentication'

import { AppStoreBanner } from '@assets/banners/AppStore.banner'
import { GooglePlayBanner } from '@assets/banners/GooglePlay.banner'
import { MicrosoftStoreBanner } from '@assets/banners/MicrosoftStore.banner'

import { Box } from '@shared/components/Box'

interface Props {
  data: GoogleAuthenticator2FAProviderDataDto | null
  isLoading?: boolean
  isError?: boolean
  onSubmit: (code: string) => void | Promise<void>
  onCancel?: () => void
}

export const GoogleAuthenticatorModal: BaseModalType<Props> = (props) => {
  const { t } = useTranslation()
  const { close, data, isLoading, isError, onSubmit } = props

  const parser = useMemo(() => new UAParser(), [])
  const { device, os } = parser.getResult()
  const { addSnackbar } = useSnackbar()

  const [code, setCode] = useState('')

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = (e.target.value || '').replace(/\D+/g, '').slice(0, 6)
    setCode(digits)
  }

  const isContinueEnabled = (code?.length ?? 0) >= 6 && !isLoading

  const handleContinue = async () => {
    if (!isContinueEnabled) return
    await onSubmit(code)
  }

  return (
    <Modal>
      <Modal.Header>{t('Profile.Set up 2FA via Authenticator App')}</Modal.Header>
      <Modal.Body isLoading={isLoading}>
        <Box flex direction='column' gap='x16'>
          <Box flex align='center' gap='x8'>
            <RichIcon
              size='x24'
              shape='circle'
              bg='surface-black'
              labelColor='text-white'
              label='1'
            />
            <Modal.Body.Text>
              {t('Trading Account.Download')}{' '}
              <Modal.Body.Text medium>{t('Profile.Google Authenticator app')}</Modal.Body.Text>
            </Modal.Body.Text>
          </Box>
          <Box flex align='center' gap='x8' wrap>
            {(() => {
              const isMobile = !!device.type
              const isIOS = os.name === 'iOS'
              const isAndroid = os.name === 'Android'

              const showApple = !isMobile || isIOS || (!isIOS && !isAndroid)
              const showAndroid = !isMobile || isAndroid || (!isIOS && !isAndroid)
              const showWindows = !isMobile && os.name === 'Windows Phone'

              return (
                <>
                  {showApple && (
                    <Link
                      href='https://apps.apple.com/us/app/google-authenticator/id388497605'
                      aria-label='Download on the App Store'
                    >
                      <AppStoreBanner height={40} width='auto' />
                    </Link>
                  )}
                  {showAndroid && (
                    <Link
                      href='https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&pcampaignid=web_share'
                      aria-label='Get it on Google Play'
                    >
                      <GooglePlayBanner height={40} width='auto' />
                    </Link>
                  )}
                  {showWindows && (
                    <Link
                      href='https://www.microsoft.com/store/productId/9NBLGGH5K7JN'
                      aria-label='Download from Microsoft Store'
                    >
                      <MicrosoftStoreBanner height={40} width='auto' />
                    </Link>
                  )}
                </>
              )
            })()}
          </Box>
        </Box>

        <Divider.Horizontal rounded />

        <Box flex direction='column' gap='x16'>
          <Box flex align='center' gap='x8'>
            <RichIcon
              size='x24'
              shape='circle'
              bg='surface-black'
              labelColor='text-white'
              label='2'
            />
            {!device.type ? (
              <Modal.Body.Text>
                {t('Profile.Scan the QR-code in the app using your device camera')}
              </Modal.Body.Text>
            ) : (
              <Modal.Body.Text>
                {t('Profile.Enter the password provided')}{' '}
                <Modal.Body.Text medium>{t('Profile.Below in the app')}</Modal.Body.Text>
              </Modal.Body.Text>
            )}
          </Box>
          <Box direction='column' flex gap='x8'>
            <Box>
              {!device.type && data?.data.qrCode && (
                <img width='120' height='120' alt={t('QR code')} src={data?.data.qrCode} />
              )}
            </Box>
            <Box>
              <Modal.Body.Text medium>
                {!device.type ? t('Profile.Or enter the code manually') : t('Profile.Manual Entry')}
              </Modal.Body.Text>
              <Input
                size='lg'
                rightIcon={CopyIcon}
                type='text'
                onEndIconClick={() => {
                  copyToClipboard(data?.data.manualToken)
                  addSnackbar.success({ message: t('Text/link copied!') })
                }}
                fullWidth
                readOnly
                value={data?.data.manualToken}
              />
            </Box>
          </Box>
        </Box>

        <Divider.Horizontal rounded />

        <Box flex direction='column' gap='x16'>
          <Box flex align='center' gap='x8'>
            <RichIcon
              size='x24'
              shape='circle'
              bg='surface-black'
              labelColor='text-white'
              label='3'
            />
            <Modal.Body.Text>
              {t('Profile.The app will start generating verification codes')}{' '}
            </Modal.Body.Text>
          </Box>
          <Modal.Body.Text medium>
            {t('Profile.Please enter one of the codes below')}
          </Modal.Body.Text>
          <Input
            label={t('Profile.Enter the verification code')}
            value={code}
            onChange={handleCodeChange}
            type='tel'
            onKeyDown={(e: any) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void handleContinue()
              }
            }}
          />
          {isError && <Body1 color='text-danger-high-em'>{t('errors.Code is not valid')}</Body1>}
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close}>{t('Cancel')}</Modal.Footer.Secondary>
        <Modal.Footer.Primary disabled={!isContinueEnabled} onClick={handleContinue}>
          {t('Continue')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
